import {announce} from '@github-ui/aria-live'
import {keepPreviousData, useQuery} from '@github-ui/react-query'
import {useDebounce} from '@github-ui/use-debounce'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import type {SimilarIssue} from '../SimilarIssues'
import {normalizeForDuplicateThreshold} from '../utils/normalize-input'
import type {IssueCreateContext} from '../utils/option-config'
import {useSimilarIssuesAnalytics} from './use-similar-issues-analytics'

export type SimilarIssueUpdate = {
  nodeId: string
  title?: string
  state?: SimilarIssue['state']
  stateReason?: SimilarIssue['stateReason']
}

export type UseSimilarIssuesOptions = {
  repoOwner: string
  repoName: string
  title: string
  body: string
  isSimilarIssuesEnabled: boolean
  context: IssueCreateContext
}

/** State for duplicate detection feature */
export type DuplicateDetectionState = {
  items: readonly SimilarIssue[]
  isError: boolean
  isDuplicateDetectionEnabled: boolean
  sessionResetKey: number
  /** Update a similar issue in the cache */
  updateIssue: (update: SimilarIssueUpdate) => void
  /** Track when the user clicks an issue */
  trackIssueClicked: (position: number) => void
  /** Reset state for a new issue creation session */
  resetForNewSession: () => void
  /** Track when the user creates an issue */
  trackIssueCreated: () => void
}

const DEFAULT_MIN_TITLE_LENGTH = 20
export const DEFAULT_MIN_BODY_LENGTH = 1000
export const DEFAULT_DEBOUNCE_MS = 1000

/**
 * Duplicate detection lifecycle.
 * - pending: Waiting for title + body threshold to be met;
 * - showing: Results are displayed in UI; Only title trigger re-check.
 * - complete: Terminal state; No results shown and no more checks for current session;
 */
export const DetectionPhase = {
  PENDING: 'pending',
  SHOWING: 'showing',
  COMPLETE: 'complete',
} as const
type DetectionPhase =
  | {status: typeof DetectionPhase.PENDING}
  | {status: typeof DetectionPhase.SHOWING; lockedItems: SimilarIssue[]; lockedTitle: string}
  | {status: typeof DetectionPhase.COMPLETE}

const INITIAL_DETECTION_PHASE: DetectionPhase = {status: DetectionPhase.PENDING}

function haveSameItems(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  const setB = new Set(b)
  return a.every(id => setB.has(id))
}

/** Given resolved data, determine the next DetectionPhase (or null if no transition needed). Resets with new session */
export function computeDetectionPhase(
  detectionPhase: DetectionPhase,
  resolvedData: SimilarIssue[],
  queryTitle: string,
  titleMeetsThreshold: boolean,
  bodyMeetsThreshold: boolean,
): DetectionPhase | null {
  if (detectionPhase.status === DetectionPhase.COMPLETE) return null
  if (detectionPhase.status === DetectionPhase.PENDING) {
    if (!(titleMeetsThreshold && bodyMeetsThreshold)) return null
    return resolvedData.length > 0
      ? {
          status: DetectionPhase.SHOWING,
          lockedItems: resolvedData,
          lockedTitle: queryTitle,
        }
      : {status: DetectionPhase.COMPLETE}
  }

  if (detectionPhase.status === DetectionPhase.SHOWING && queryTitle !== detectionPhase.lockedTitle) {
    const currentNodeIds = resolvedData.map(item => item.nodeId)
    const lockedNodeIds = detectionPhase.lockedItems.map(item => item.nodeId)
    return !haveSameItems(currentNodeIds, lockedNodeIds)
      ? {status: DetectionPhase.COMPLETE}
      : {...detectionPhase, lockedTitle: queryTitle}
  }

  return null
}

/** Holds analytics state per issue creation session. Resets on new session. */
type AnalyticsState = {
  wasIssueClicked: boolean
  issueClickCount: number
  hadDuplicateMatches: boolean
}

const initialAnalyticsState: AnalyticsState = {
  wasIssueClicked: false,
  issueClickCount: 0,
  hadDuplicateMatches: false,
}

// Stable empty array to prevent infinite re-render loops when items are empty
const EMPTY_ITEMS: SimilarIssue[] = []

export function useSimilarIssues({
  repoOwner,
  repoName,
  title,
  body,
  isSimilarIssuesEnabled,
  context,
}: UseSimilarIssuesOptions): DuplicateDetectionState {
  const previousCountRef = useRef<number | null>(null)

  const [queryTitle, setQueryTitle] = useState(title.trim())
  const [queryBody, setQueryBody] = useState(body.trim())

  // Session ID ref for correlating analytics events.
  // Only called from effects/handlers, never during render (SSR-safe in current usage).
  const sessionIdRef = useRef<string | undefined>(undefined)
  const getSessionId = useCallback(() => (sessionIdRef.current ??= crypto.randomUUID()), [])

  // Counter to reset components on new session — SSR-safe (starts at 0 on both server and client).
  const [sessionResetKey, setSessionResetKey] = useState(0)
  const [detectionPhase, setDetectionPhase] = useState<DetectionPhase>(INITIAL_DETECTION_PHASE)

  const analyticsRef = useRef<AnalyticsState>({...initialAnalyticsState})

  const {sendRequestComplete, sendIssueClicked, sendIssueCreated} = useSimilarIssuesAnalytics(
    context,
    `${repoOwner}/${repoName}`,
  )

  const debounceQueryTitleUpdate = useDebounce((value: string) => setQueryTitle(value.trim()), DEFAULT_DEBOUNCE_MS)
  const debounceQueryBodyUpdate = useDebounce((value: string) => setQueryBody(value.trim()), DEFAULT_DEBOUNCE_MS)

  useEffect(() => {
    if (!isSimilarIssuesEnabled) return
    if (detectionPhase.status === DetectionPhase.COMPLETE) return
    debounceQueryTitleUpdate(title.trim())
  }, [isSimilarIssuesEnabled, title, debounceQueryTitleUpdate, detectionPhase.status])

  useEffect(() => {
    if (!isSimilarIssuesEnabled) return
    if (detectionPhase.status === DetectionPhase.COMPLETE) return
    debounceQueryBodyUpdate(body.trim())
  }, [isSimilarIssuesEnabled, body, debounceQueryBodyUpdate, detectionPhase.status])

  const normalizedQueryBody = useMemo(() => normalizeForDuplicateThreshold(queryBody), [queryBody])
  const titleMeetsThreshold = queryTitle.trim().length >= DEFAULT_MIN_TITLE_LENGTH
  const bodyMeetsThreshold = normalizedQueryBody.length >= DEFAULT_MIN_BODY_LENGTH

  // Determine if we should check for duplicates based on current state
  const shouldFetch = useMemo(() => {
    switch (detectionPhase.status) {
      case DetectionPhase.PENDING:
        return titleMeetsThreshold && bodyMeetsThreshold
      case DetectionPhase.SHOWING:
        return queryTitle !== detectionPhase.lockedTitle
      case DetectionPhase.COMPLETE:
        return false
    }
  }, [detectionPhase, queryTitle, titleMeetsThreshold, bodyMeetsThreshold])

  const queryKey = useMemo(
    () =>
      [
        'similar-issues',
        repoOwner,
        repoName,
        queryTitle,
        shouldFetch ? queryBody : '', // Avoids caching large body strings when not actually fetching.
        sessionResetKey,
        context,
      ] as const,
    [repoOwner, repoName, queryTitle, shouldFetch, queryBody, sessionResetKey, context],
  )
  const {data, isFetching, isError, isPlaceholderData} = useQuery({
    queryKey,
    queryFn: async ({signal}) => {
      const titleLength = queryTitle.length
      const bodyLength = queryBody.length

      const response = await verifiedFetchJSON(`/${repoOwner}/${repoName}/issues/semantically_similar`, {
        method: 'POST',
        body: {
          search: {
            title: queryTitle,
            body: queryBody,
            page: 1,
            per_page: 3,
            context,
          },
        },
        signal,
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch similar issues: ${response.status}`)
      }

      const json = await response.json()
      // Response shape: { similar_issues: [{ issue: { id, node_id, number, title, state, ... }, score: number | null }] }
      const similarIssues = json.similar_issues ?? []
      const results = similarIssues.map(
        (item: {
          issue: {
            id: number
            node_id: string
            number: number
            title: string
            state: string
            state_reason: string | null
          }
          score: number | null
        }) => ({
          id: String(item.issue.id),
          nodeId: item.issue.node_id,
          number: item.issue.number,
          title: item.issue.title,
          state: item.issue.state.toUpperCase(),
          stateReason: item.issue.state_reason?.toUpperCase(),
          score: item.score,
        }),
      ) as SimilarIssue[]

      sendRequestComplete(getSessionId(), results.length, titleLength, bodyLength)
      // Track if we ever got results in this session
      if (results.length > 0) {
        analyticsRef.current.hadDuplicateMatches = true
      }

      return results
    },
    enabled: isSimilarIssuesEnabled && shouldFetch,
    staleTime: 60 * 1000, // 1 minute,
    placeholderData: keepPreviousData,
  })

  // When `data` changes, we need detectionPhase to be up-to-date *before* the render
  // paints, so `items` reflects the correct state. By computing the state transition
  // during render (not in useEffect), React batches both updates into a single paint.
  // See: https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const resolvedFetchData = !isFetching && !isPlaceholderData ? data : undefined
  const [prevResolvedData, setPrevResolvedData] = useState<SimilarIssue[] | undefined>(undefined)
  if (isSimilarIssuesEnabled && resolvedFetchData !== prevResolvedData) {
    setPrevResolvedData(resolvedFetchData)
    if (resolvedFetchData !== undefined) {
      const nextState = computeDetectionPhase(
        detectionPhase,
        resolvedFetchData,
        queryTitle,
        titleMeetsThreshold,
        bodyMeetsThreshold,
      )
      if (nextState) setDetectionPhase(nextState)
    }
  }
  // If title is cleared during render, permanently hide until new session
  const [prevTitle, setPrevTitle] = useState(title)
  if (isSimilarIssuesEnabled && title !== prevTitle) {
    setPrevTitle(title)
    if (detectionPhase.status === DetectionPhase.SHOWING && title.trim().length === 0) {
      setDetectionPhase({status: DetectionPhase.COMPLETE})
    }
  }

  const items = detectionPhase.status === DetectionPhase.SHOWING ? detectionPhase.lockedItems : EMPTY_ITEMS

  // Update a displayed issue when subscription data arrives
  const updateIssue = useCallback((update: SimilarIssueUpdate) => {
    setDetectionPhase(prev => {
      if (prev.status !== DetectionPhase.SHOWING) return prev
      return {
        ...prev,
        lockedItems: prev.lockedItems.map(item => {
          if (item.nodeId !== update.nodeId) return item
          return {
            ...item,
            ...(update.title !== undefined && {title: update.title}),
            ...(update.state !== undefined && {state: update.state}),
            ...(update.stateReason !== undefined && {stateReason: update.stateReason}),
          }
        }),
      }
    })
  }, [])

  useEffect(() => {
    if (!isSimilarIssuesEnabled) return
    if (detectionPhase.status === DetectionPhase.PENDING) return

    if (!isFetching && previousCountRef.current !== items.length) {
      previousCountRef.current = items.length
      const count = items.length
      if (count === 0) {
        announce('No potential duplicates')
      } else if (count === 1) {
        announce('1 potential duplicate')
      } else {
        announce(`${count} potential duplicates`)
      }
    }
  }, [detectionPhase.status, isSimilarIssuesEnabled, items.length, isFetching])

  // Track issue click
  const trackIssueClicked = useCallback(
    (position: number) => {
      analyticsRef.current.wasIssueClicked = true
      analyticsRef.current.issueClickCount += 1
      sendIssueClicked(getSessionId(), position)
    },
    [sendIssueClicked, getSessionId],
  )

  // Track interactions leading up to issue creation
  const trackIssueCreated = useCallback(() => {
    const analyticsState = analyticsRef.current
    sendIssueCreated(getSessionId(), {
      hadDuplicateMatches: analyticsState.hadDuplicateMatches,
      wasIssueClicked: analyticsState.wasIssueClicked,
      issueClickCount: analyticsState.issueClickCount,
    })
  }, [sendIssueCreated, getSessionId])

  /** Handles resetting feedback, analytics, and expand state of the section when 'Create more' is enabled. */
  const resetForNewSession = useCallback(() => {
    previousCountRef.current = null

    setQueryTitle('')
    setQueryBody('')
    setDetectionPhase(INITIAL_DETECTION_PHASE)

    analyticsRef.current = {...initialAnalyticsState}
    sessionIdRef.current = crypto.randomUUID()
    setSessionResetKey(prev => prev + 1)
  }, [])

  return {
    items,
    isError,
    isDuplicateDetectionEnabled: isSimilarIssuesEnabled,
    sessionResetKey,
    updateIssue,
    trackIssueClicked,
    resetForNewSession,
    trackIssueCreated,
  }
}
